import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5m9krn72m0gvhnvdk5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setMessageLength(0);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const length = e.target.value.length;
    if (length <= 500) {
      setMessageLength(length);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Zen_Maru_Gothic']">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <a href="#company" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              会社情報
            </a>
            <a href="#access" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              アクセス
            </a>
          </div>
          <button 
            onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
            className={`px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer ${isScrolled ? 'bg-white text-gray-900' : 'bg-white text-gray-900'}`}
          >
            見学予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/bcf5b92fc084d7282b4356fb1f4187de.png"
            alt="SmALL展示会"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex flex-col justify-between px-6 lg:pl-16 md:pl-12 pl-8">
          <div className="pt-32 sm:pt-40 md:pt-48 lg:pt-32">
            <div className="max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                愛犬ともっと<br />自由に暮らす家<br />ミニラボ
              </h1>
            </div>
          </div>
          
          {/* ボタンを画面下部に配置 */}
          <div className="pb-16 sm:pb-20 md:pb-24 lg:pb-20 max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl">
            <button 
              onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-gray-900 px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-semibold hover:shadow-2xl transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer"
            >
              今すぐ見学予約
              <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-lg font-semibold text-gray-600 mb-4 uppercase tracking-wider">
                こんな心配はありませんか？
              </div>
              <h2 className="text-3xl font-bold text-gray-900 leading-tight">
                わんちゃんと住みたいけど…
              </h2>
            </div>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">ペット可の賃貸物件が少ない</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">近隣への鳴き声が気になる</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">お留守番が心配</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exhibition Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">ミニラボ展示会情報</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer">
              <div className="w-full">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/e07cf24cf8c7b011edd4eafea3da042a.jpeg"
                  alt="外観デザイン"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-lg font-bold">外観写真</h3>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8 flex flex-col justify-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">ミニラボ展示会</h3>
                  <p className="text-gray-600">ペット共生住宅を実際に体験</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">開催日</div>
                      <div className="text-lg text-gray-900">5月中旬（決まり次第にLINEでご連絡いたします）</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">開催時間</div>
                      <div className="text-lg text-gray-900">10:00～16:00</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">住所</div>
                      <div className="text-lg text-gray-900">秋田県湯沢市田町2-1-3</div>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  見学予約する
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-6 py-3 rounded-full text-lg font-semibold mb-4">
              メリット
            </div>
            <h2 className="text-3xl font-bold text-gray-900">3つの特徴</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-48">
                <img
                  src="https://public.readdy.ai/ai/img_res/b07456e9a09591fd179c30bdf76d7e86.jpg"
                  alt="ペット共生住宅"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">ペット共生住宅</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  賃貸では難しかったワンちゃんと一緒に住めます。
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20soundproof%20wall%20construction%20detail%20showing%20high-quality%20insulation%20materials%20and%20double-pane%20glass%20windows%20in%20a%20contemporary%20Japanese%20home%20interior%2C%20clean%20minimalist%20design%20with%20white%20walls%20and%20natural%20wood%20accents%2C%20technical%20yet%20aesthetic%20presentation%20emphasizing%20sound%20insulation%20technology%2C%20simple%20and%20clean%20background&width=400&height=300&seq=soundproof-feature-002&orientation=landscape"
                  alt="優れた遮音性"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">優れた遮音性</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  高断熱仕様の壁とペアガラスにより、鳴き声が外に漏れないのを抑えます。
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cute%20small%20dog%20resting%20comfortably%20in%20a%20warm%20and%20cozy%20modern%20Japanese%20container%20house%20interior%20during%20winter%2C%20with%20visible%20air%20conditioning%20unit%20maintaining%20comfortable%20temperature%2C%20minimalist%20white%20walls%20and%20wooden%20flooring%2C%20soft%20natural%20lighting%20creating%20a%20peaceful%20atmosphere%2C%20simple%20and%20clean%20background%20emphasizing%20thermal%20insulation%20comfort%20for%20pets%20left%20at%20home&width=400&height=300&seq=insulation-feature-003&orientation=landscape"
                  alt="高断熱コンテナ工法"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">高断熱コンテナ工法</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  冬でもエアコン1台で留守番中のワンちゃんを寒さから守ります。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-5 py-2 rounded-full text-lg font-semibold mb-4">
              プラン紹介
            </div>
            <h2 className="text-3xl font-bold text-gray-900">商品プラン・価格</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Residential Plan */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="bg-teal-600 text-white py-6 px-8">
                <h3 className="text-3xl font-bold text-center">住居プラン</h3>
              </div>
              <div className="p-8">
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">外観デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/4d8fb089c6ba4332ce03bc206d502495.jpeg"
                      alt="住居プラン外観"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">内装デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/56c66290d59ae98c1f7d29e4b89fc0b7.jpeg"
                      alt="住居プラン内装"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">間取りプラン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/a4bd3d1362707523f0acb630c7239fae.png"
                      alt="店舗プラン間取り"
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                  <div className="text-lg text-gray-600 mt-2">21.05㎡(6.36坪)</div>
                </div>
                <div className="bg-teal-50 rounded-2xl p-6 text-center mb-6">
                  <div className="text-sm font-semibold text-teal-600 mb-2">コミコミ価格</div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    1,098<br />
                    <span className="text-2xl">万円〜</span>
                  </div>
                  <div className="text-sm text-gray-600">(税込・基礎工事・付帯工事・エアコン含む)</div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-4 text-center">お支払い例</div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">頭金０円・金利1.55％・借入金1,098万円の場合</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">35年ローン</span>
                      <span className="text-lg font-bold text-gray-900">月々 33,888円</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">※お支払い例は条件により変動します。</p>
                </div>
                <button 
                  onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  このプランで見学予約
                </button>
              </div>
            </div>

            {/* Store Plan */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="bg-amber-600 text-white py-6 px-8">
                <h3 className="text-3xl font-bold text-center">店舗プラン</h3>
              </div>
              <div className="p-8">
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">外観デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/779a25bce501d00ce6ec29d8a1ec512d.jpeg"
                      alt="店舗プラン外観"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">内装デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/57167f92b0fd08f325f5e5e347676733.jpeg"
                      alt="店舗プラン内装"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">間取りプラン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full h-64">
                    <img
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/a67402a06b41a991d1bc4a3e19416127.png"
                      alt="店舗プラン間取り"
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                  <div className="text-lg text-gray-600 mt-2">21.05㎡(6.36坪)</div>
                </div>
                <div className="bg-amber-50 rounded-2xl p-6 text-center mb-6">
                  <div className="text-sm font-semibold text-amber-600 mb-2">コミコミ価格</div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    1,045<br />
                    <span className="text-2xl">万円〜</span>
                  </div>
                  <div className="text-sm text-gray-600">(税込・基礎工事・付帯工事・エアコン含む)</div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-4 text-center">店舗プランの用途例</div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <i className="ri-check-line text-gray-600 mt-0.5"></i>
                      <span className="text-sm text-gray-600">トリミングサロン</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-check-line text-gray-600 mt-0.5"></i>
                      <span className="text-sm text-gray-600">ドッグカフェ</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-check-line text-gray-600 mt-0.5"></i>
                      <span className="text-sm text-gray-600">ペットホテル</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-amber-600 text-white py-4 rounded-lg font-semibold hover:bg-amber-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  このプランで見学予約
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16 items-center">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 leading-tight">
                高断熱コンテナ工法で１日で設置完了
              </h2>
              <div className="mt-8 rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/ebe4c57daf3373266969e6f13e093b9a.png"
                  alt="高断熱コンテナ工法"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-3 grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">工場生産の木造コンテナ住宅であるため設置はわずか1日。必要に応じて運搬移設が可能です。</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">トリマーやドッグカフェの店舗などにも広範囲な活用が可能です。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">こんな方におすすめ</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cheerful%20Japanese%20woman%20in%20her%2030s%20with%20long%20black%20hair%20smiling%20warmly%20while%20holding%20a%20small%20cute%20dog%20in%20her%20arms%2C%20standing%20in%20front%20of%20a%20modern%20minimalist%20white%20Japanese%20home%20with%20wooden%20accents%2C%20bright%20natural%20sunlight%20creating%20a%20warm%20and%20welcoming%20atmosphere%2C%20expressing%20happiness%20and%20the%20joy%20of%20pet%20ownership%2C%20clean%20simple%20background%20emphasizing%20the%20loving%20bond%20between%20the%20Japanese%20owner%20and%20her%20adorable%20pet%20dog&width=400&height=350&seq=japanese-woman-dog-001&orientation=landscape"
                  alt="自分の住まいを持ちたい方"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8 text-center">
                <p className="text-gray-700 leading-relaxed">
                  賃貸ではペット可物件が少なく、自分とワンちゃんだけの安心できる住まいを持ちたい方
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20peaceful%20scene%20of%20a%20happy%20dog%20barking%20playfully%20inside%20a%20modern%20soundproof%20Japanese%20home%20interior%2C%20owner%20looking%20relaxed%20and%20stress-free%2C%20clean%20minimalist%20design%20with%20white%20walls%20and%20wooden%20flooring%2C%20natural%20lighting%20creating%20a%20calm%20atmosphere%2C%20simple%20and%20clean%20background%20emphasizing%20soundproofing%20and%20peace%20of%20mind&width=400&height=350&seq=target-audience-002&orientation=landscape"
                  alt="防音性能を求める方"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8 text-center">
                <p className="text-gray-700 leading-relaxed">
                  戸建てで防音性能が高くワンちゃんの鳴き声など周りに気にせず安心して暮らしたい方
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-full h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20comfortable%20and%20content%20dog%20resting%20alone%20in%20a%20cozy%20modern%20Japanese%20home%20interior%20while%20owner%20is%20away%2C%20warm%20and%20well-insulated%20space%20with%20air%20conditioning%2C%20minimalist%20white%20walls%20and%20wooden%20flooring%2C%20soft%20natural%20lighting%20creating%20a%20stress-free%20environment%2C%20simple%20and%20clean%20background%20emphasizing%20comfort%20and%20thermal%20insulation%20for%20pets%20left%20at%20home&width=400&height=350&seq=target-audience-003&orientation=landscape"
                  alt="快適な住環境を求める方"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-8 text-center">
                <p className="text-gray-700 leading-relaxed">
                  お留守番でも、ワンちゃんがストレスなく快適に過ごせる住環境が欲しい方
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-5 py-2 rounded-full text-lg font-semibold mb-4">
              よくある質問
            </div>
            <h2 className="text-4xl font-bold text-gray-900">Q&A</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">工事にどれくらい時間がかかりますか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  工場で木造コンテナを組み立て、現地の基礎の上に設置するため、トータル2カ月前後でのお引渡しが可能です。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">建物価格はどれくらいですか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  建物は、税込1098万からご提案しています。この価格には基礎工事など必要な工事費も含まれます。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">住宅ローンは使えますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  はい、住宅ローンをご利用できます。資金面でご不安があれば専門スタッフがご相談に応じますのでご安心ください。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">設置できる場所に制限はありますか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  トラック輸送するため道路幅やクレーン作業での電線などの検討が必要です。専門スタッフが現地調査を行い、設置可能かどうか確認します。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q5</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">断熱材はどうなっていますか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  断熱材は、2030年対応の等級5(ZEH)が標準仕様。夏は涼しく冬は暖かい室内空間になっております。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q6</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">施工後の保証やアフターサービスはありますか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  住まいの品質と安心を保つために、お引渡し後は、構造体10年保証や水回り設備保証などのサポート体制も整えています。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q7</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">秋田県内での施工エリアはどこまでですか?</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  設置可能な地域については、スタッフがご相談の上、対応可能エリアを確認します。遠方の場合もご相談ください。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q8</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">6坪って本当に住めるの？狭くないですか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  天井も高く、大きな窓もあるので解放感があります。広さは実物のミニラボでご体感ください。
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q9</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">家族が増えたら増築はできますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">
                  はい。将来、ご家族が増えた場合には、増築ユニットをご用意しておりますのでご安心ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-amber-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block bg-white/80 backdrop-blur-sm text-amber-900 px-5 py-2 rounded-full text-sm font-semibold mb-6">
            特典
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 leading-tight">
            今なら来場予約で、<br />クオカード500円プレゼント
          </h2>
          <button 
            onClick={() => document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-all flex items-center gap-3 mx-auto whitespace-nowrap cursor-pointer"
          >
            見学予約はこちら
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section id="reservation" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16">
            <div className="md:col-span-2">
              <div className="text-sm font-semibold text-gray-600 mb-4 uppercase tracking-wider">見学予約</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                展示会を<br />体験してください
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                実際のミニラボをご覧ください。専門スタッフが丁寧にご案内いたします。
              </p>
              <p className="text-gray-600 leading-relaxed">
                ご予約いただいた方には、もれなくクオカード500円をプレゼントいたします。
              </p>
            </div>
            <div className="md:col-span-3">
              <form 
                className="bg-white rounded-2xl p-8 shadow-xl" 
                data-readdy-form={true}
                id="reservation-form"
                onSubmit={handleSubmit}
              >
                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800">
                    予約を受け付けました。ありがとうございます！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                    送信に失敗しました。もう一度お試しください。
                  </div>
                )}
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="example@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="datetime" className="block text-sm font-semibold text-gray-700 mb-2">
                      見学予約日時 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="datetime"
                      name="datetime"
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      ご質問、ご要望
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={handleTextareaChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm"
                      placeholder="ご質問やご要望がございましたらご記入ください(500文字以内)"
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1 text-right">{messageLength}/500</div>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '送信中...' : '予約を確定する'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="company" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-8">運営会社情報</h2>
              <div className="space-y-6">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">運営会社</div>
                  <div className="text-lg text-gray-900">湯沢開発株式会社</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">代表者</div>
                  <div className="text-lg text-gray-900">高橋正明</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">所在地</div>
                  <div className="text-lg text-gray-900">秋田県湯沢市田町2-1-3</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">電話番号</div>
                  <div className="text-lg text-gray-900">0183-73-7125</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">定休日</div>
                  <div className="text-lg text-gray-900">水曜日</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">創業</div>
                  <div className="text-lg text-gray-900">1951年</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">宅地建物取引業許可</div>
                  <div className="text-lg text-gray-900">秋田県知事免許(17)第43号</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">建設業登録</div>
                  <div className="text-lg text-gray-900">秋田県知事許可(般-4)第80084号</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">一級建築士事務所</div>
                  <div className="text-lg text-gray-900">秋田県知事登録 第16-110A-0250号</div>
                </div>
              </div>
            </div>
            <div id="access">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">ミニラボ展示会場アクセス</h2>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d1546.8092463002413!2d140.49084285597093!3d39.160658072616755!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMznCsDA5JzM4LjQiTiAxNDDCsDI5JzMxLjciRQ!5e0!3m2!1sja!2sjp!4v1765608274945!5m2!1sja!2sjp"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="展示会アクセスマップ"
                ></iframe>
              </div>
              <p className="text-sm text-gray-600 mt-4">秋田県湯沢市田町2-1-3</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-xl font-bold mb-6">ペット共生住宅ミニラボ</h3>
              <p className="text-teal-100 leading-relaxed mb-6">
                月々3万円台からわんちゃんと一緒に住める家
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">お問い合わせ</h4>
              <div className="space-y-3 text-teal-100">
                <p>秋田県湯沢市田町2-1-3</p>
                <p>TEL: 0183-73-7125</p>
                <p>定休日: 水曜日</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">営業時間</h4>
              <div className="space-y-2 text-teal-100">
                <p>水曜日:定休日</p>
              </div>
            </div>
          </div>
          <div className="border-t border-teal-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-teal-200">Copyright © 2025 湯沢開発株式会社</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
